import {
  parseInstagramUsername,
  parseTiktokUsername,
  type ProductFormLocation,
  type ProductFormValues,
  type ProductRoutingMode,
} from '@up2stay/up2promo-commons/products';

/**
 * API still requires a non-empty description. For standee setup we derive one
 * from the routing target when the operator leaves the optional label blank.
 */
export function resolveSetupDescription(params: {
  description: string;
  routingMode: ProductRoutingMode;
  values: ProductFormValues;
  locations: ProductFormLocation[];
  fallback: string;
}): string {
  const trimmed = params.description.trim();
  if (trimmed) return trimmed;

  switch (params.routingMode) {
    case 'location': {
      const loc = params.locations.find(
        (l) => l.id === params.values.locationId,
      );
      const name = loc?.name?.trim();
      if (name) return name;
      break;
    }
    case 'instagramUsername': {
      const user = parseInstagramUsername(params.values.targetUrl);
      if (user) return `@${user}`;
      break;
    }
    case 'tiktokUsername': {
      const user = parseTiktokUsername(params.values.targetUrl);
      if (user) return `@${user}`;
      break;
    }
    case 'customUrl': {
      const host = hostOf(params.values.targetUrl);
      if (host) return host;
      break;
    }
    case 'profile':
      break;
    default:
      break;
  }

  // Any other mode (e.g. "Eigener Link" picked as the type, which routes
  // via `behaviourSelect`) still names the product after its target instead
  // of the generic fallback (DEV-568).
  const location = params.locations.find(
    (l) => l.id === params.values.locationId,
  );
  const locationName = location?.name?.trim();
  if (locationName) return locationName;
  const host = hostOf(params.values.targetUrl);
  if (host) return host;

  return params.fallback;
}

function hostOf(targetUrl: string): string | undefined {
  const trimmed = targetUrl.trim();
  if (!trimmed) return undefined;
  try {
    const host = new URL(
      trimmed.startsWith('http') ? trimmed : `https://${trimmed}`,
    ).hostname.replace(/^www\./, '');
    return host || undefined;
  } catch {
    return undefined;
  }
}
